import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Button, PlayerIconArrowRight } from "@mebi/ui";

const sectionHeaderVariants = cva(
  "flex items-center justify-between w-full min-h-[25px] mb-4",
  {
    variants: {
      size: {
        md: "gap-4 mb-4",
        sm: "gap-2 md:gap-3 mb-3",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const sectionTitleVariants = cva(
  "text-base font-semibold text-Color-Text-neutral-black leading-6",
  {
    variants: {
      size: {
        md: "text-base font-semibold", // 16px/24px Semibold
        lg: "text-lg font-semibold leading-7", // 18px/28px Semibold
        sm: "text-sm font-semibold", // 14px/22px Semibold
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export interface SectionHeaderProps {
  /** Section title text */
  title: string;
  /** Show yellow stripe indicator on the left */
  showStripe?: boolean;
  /** Optional action button on the right */
  actionButton?: {
    text: string;
    onClick: () => void;
    variant?: "primary" | "secondary" | "tertiary";
    disabled?: boolean;
    loading?: boolean;
  };
  /** Optional action link on the right (replaces actionButton) */
  actionLink?: {
    text: string;
    onClick: () => void;
  };
  /** Size variant */
  size?: "md" | "sm";
  /** Title size variant */
  titleSize?: "md" | "lg" | "sm";
  /** Additional CSS classes */
  className?: string;
}

const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  (
    {
      title,
      showStripe = false,
      actionButton,
      actionLink,
      size = "md",
      titleSize = "md",
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(sectionHeaderVariants({ size }), className)}
        {...props}
      >
        {/* Left section - Stripe + Title */}
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {showStripe && (
            <div
              className="w-1 h-6 bg-Color-Background-brand-default rounded-[999px] flex-shrink-0"
              aria-hidden="true"
            />
          )}
          <h2
            className={cn(
              sectionTitleVariants({ size: titleSize }),
              "truncate", // Prevent text overflow on mobile
            )}
          >
            {title}
          </h2>
        </div>

        {/* Right section - Action Button */}
        {actionButton && !actionLink && (
          <div className="flex-shrink-0 ml-2">
            {" "}
            <Button
              variant={actionButton.variant || "primary"}
              size="sm"
              onClick={actionButton.onClick}
              disabled={actionButton.disabled}
              loading={actionButton.loading}
              className="h-5 px-2 text-xs font-semibold whitespace-nowrap rounded-lg"
            >
              {actionButton.text}
            </Button>
          </div>
        )}

        {/* Right section - Action Link */}
        {actionLink && (
          <button
            type="button"
            className="flex justify-center items-center gap-1 cursor-pointer flex-wrap content-center ml-2 appearance-none bg-transparent border-none p-0"
            onClick={actionLink.onClick}
          >
            <div className="text-center justify-center text-Color-Text-brand-default text-sm font-semibold font-['Be_Vietnam_Pro'] underline leading-5">
              {actionLink.text}
            </div>
            <div className="w-4 h-4 relative flex items-center justify-center overflow-hidden">
               <PlayerIconArrowRight className="w-4 h-4 text-Color-Icon-brand" />
            </div>
          </button>
        )}
      </div>
    );
  },
);

SectionHeader.displayName = "SectionHeader";

export { SectionHeader };
